import { useMemo, useRef, useState } from 'react';
import {
  buildShareUrl,
  buildEmailShareUrl,
  buildSocialShareUrl,
  nativeShare,
  track,
} from '../utils/tracking.js';
import { playClick } from '../utils/audio.js';

const SHARE_TITLE = 'OpenEd State Capital Quiz';

// `navigator.share` is available on iOS Safari, Android Chrome, and most
// modern mobile browsers. We use it as the primary share path on mobile —
// it's far more reliable than clipboard.writeText, which is gated by HTTPS
// and user-gesture context and silently fails in quite a few WebViews.
function detectNativeShare() {
  if (typeof navigator === 'undefined' || !navigator.share) return false;
  if (typeof window === 'undefined') return false;
  // Heuristic: only treat as primary on touch / coarse-pointer devices.
  // Desktop Safari technically has navigator.share too but the sheet is awkward.
  if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return true;
  // Fall back to a UA sniff for iPad-as-desktop quirks.
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
}

export default function ReplayGate({ onPlayAgain, result }) {
  const shareUrl = useMemo(() => buildShareUrl({ medium: 'share', source: 'replay_gate' }), []);
  const inputRef = useRef(null);
  const [copyState, setCopyState] = useState('idle'); // 'idle' | 'copied' | 'selected' | 'error'

  const showNativePrimary = useMemo(() => detectNativeShare(), []);

  async function handleNativeShare() {
    playClick();
    track('refer_clicked', { medium: 'native', score: result.score });
    const ok = await nativeShare({
      title: SHARE_TITLE,
      text: `I got ${result.score}/${result.total} on the OpenEd State Capital Quiz. How well do you know your states?`,
      url: shareUrl,
    });
    if (!ok) {
      // Native sheet refused or the user cancelled — fall back to copy.
      handleCopy();
    }
  }

  async function handleCopy() {
    playClick();
    track('refer_clicked', { medium: 'copy', score: result.score });

    // First try: real clipboard write (requires HTTPS + active document focus).
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopyState('copied');
        setTimeout(() => setCopyState('idle'), 2200);
        return;
      }
    } catch {
      /* swallow and fall through */
    }

    // Fallback: select the URL in the input so the user can ⌘C / long-press.
    const el = inputRef.current;
    if (el) {
      try {
        el.removeAttribute('readonly');
        el.focus();
        el.select();
        el.setSelectionRange(0, shareUrl.length);
        // Try execCommand as a last-ditch — works on older Safari.
        const ok = document.execCommand && document.execCommand('copy');
        el.setAttribute('readonly', 'readonly');
        setCopyState(ok ? 'copied' : 'selected');
        setTimeout(() => setCopyState('idle'), 3500);
        return;
      } catch {
        /* fall through to error */
      }
    }
    setCopyState('error');
    setTimeout(() => setCopyState('idle'), 3500);
  }

  function handleEmailRefer() {
    playClick();
    track('refer_clicked', { medium: 'email', score: result.score });
    window.location.href = buildEmailShareUrl({ score: result.score, total: result.total });
  }

  function handleSocialShare() {
    playClick();
    track('refer_clicked', { medium: 'facebook', score: result.score });
    window.open(buildSocialShareUrl('facebook'), '_blank', 'noopener,noreferrer');
  }

  function handleSkip() {
    playClick();
    track('replay_skipped');
    onPlayAgain();
  }

  const copyLabel =
    copyState === 'copied'   ? 'Link copied ✓'
    : copyState === 'selected' ? 'Link selected — ⌘C / Ctrl+C to copy'
    : copyState === 'error'    ? 'Copy unavailable — long-press the link above'
    : 'Copy link';

  return (
    <div className="replay-gate fade-in delay-4">
      <h2>Play again?</h2>

      <div className="share-link-row">
        <input
          ref={inputRef}
          className="share-link-input"
          type="text"
          readOnly
          value={shareUrl}
          aria-label="Your share link"
          onFocus={(e) => e.target.select()}
        />
      </div>

      <div className="gate-options">
        {showNativePrimary && (
          <button className="gate-primary" onClick={handleNativeShare}>
            <span className="gate-icon">↗</span>
            <div className="gate-body">
              <div className="gate-title">Share challenge</div>
              <div className="gate-sub">Open your phone's share menu — texts, DMs, anywhere.</div>
            </div>
          </button>
        )}

        <button
          className={showNativePrimary ? 'gate-secondary' : 'gate-primary'}
          onClick={handleCopy}
        >
          <span className="gate-icon">⎘</span>
          <div className="gate-body">
            <div className="gate-title">{copyLabel}</div>
            <div className="gate-sub">Paste it into any chat or text.</div>
          </div>
        </button>

        <button className="gate-secondary" onClick={handleEmailRefer}>
          <span className="gate-icon">✉</span>
          <div className="gate-body">
            <div className="gate-title">Email a friend</div>
            <div className="gate-sub">Opens your email with a personal link.</div>
          </div>
        </button>

        <button className="gate-secondary" onClick={handleSocialShare}>
          <span className="gate-icon">f</span>
          <div className="gate-body">
            <div className="gate-title">Share on Facebook</div>
            <div className="gate-sub">Tag @openedhq. Brag a little, or commiserate.</div>
          </div>
        </button>
      </div>

      <div className="gate-skip">
        <button className="gate-skip-btn" onClick={handleSkip}>
          Just let me play again
        </button>
      </div>
    </div>
  );
}
